import { useMutation } from '@tanstack/react-query';
import { Button, Form, Input, InputNumber, message, Modal, Popconfirm, Space } from 'antd';
import { DataTable } from 'common/data-table';
import menuItemApiCall from 'apps/investcore/apiCalls/home-widget-menu-items';
import { useFetchApiList } from 'hooks/api';
import { useEffect, useState } from 'react';

const MenuItemFormModal = ({
  open,
  onClose,
  homeWidgetId,
  item,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  homeWidgetId: string;
  item?: any;
  onSaved: () => void;
}) => {
  const [form] = Form.useForm();
  const isEdit = !!item;

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue(
      item
        ? {
            title: item.title,
            icon: item.icon,
            highlight: item.highlight,
            screen_name: item.screen_name,
            ordering: item.ordering,
          }
        : { ordering: 0 }
    );
  }, [open, item]);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: any) =>
      isEdit
        ? menuItemApiCall.edit.queryFn({ id: item.id, payload })
        : menuItemApiCall.create.queryFn({
            payload: { ...payload, home_widget_id: homeWidgetId },
          }),
    onSuccess: () => {
      message.success(isEdit ? 'Menu item updated' : 'Menu item created');
      form.resetFields();
      onSaved();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to save menu item');
    },
  });

  return (
    <Modal
      title={isEdit ? 'Edit Menu Item' : 'Add Menu Item'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={values => mutate(values)}>
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="icon" label="Icon" tooltip="Icon component name, e.g. ETFSVG">
          <Input />
        </Form.Item>
        <Form.Item name="highlight" label="Highlight">
          <Input />
        </Form.Item>
        <Form.Item
          name="screen_name"
          label="Screen Name"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="ordering" label="Ordering">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item className="mb-0">
          <Space className="w-full justify-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Save
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const MenuItemsSection = ({ homeWidgetId }: { homeWidgetId: string }) => {
  const {
    data: items,
    load,
    refresh,
  } = useFetchApiList(menuItemApiCall.list, { resourceName: 'data' });

  useEffect(() => {
    if (homeWidgetId) {
      load({ home_widget_id: homeWidgetId });
    }
  }, [homeWidgetId]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(undefined);

  const { mutate: deleteItem, isPending: isDeleting } = useMutation({
    mutationFn: (params: { id: string }) => menuItemApiCall.delete.queryFn(params),
    onSuccess: () => {
      message.success('Menu item deleted');
      refresh();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to delete menu item');
    },
  });

  const columns = [
    { accessorKey: 'title', header: 'Title' },
    { accessorKey: 'icon', header: 'Icon' },
    { accessorKey: 'highlight', header: 'Highlight' },
    { accessorKey: 'screen_name', header: 'Screen Name' },
    { accessorKey: 'ordering', header: 'Ordering' },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingItem(row.original);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this menu item?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteItem({ id: row.original.id })}
          >
            <Button size="small" danger loading={isDeleting}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="pt-4 border-t mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-gray-800">Menu Items</h3>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setEditingItem(undefined);
            setModalOpen(true);
          }}
        >
          Add Menu Item
        </Button>
      </div>
      <DataTable noViewOptions data={items || []} columns={columns} />
      <MenuItemFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        homeWidgetId={homeWidgetId}
        item={editingItem}
        onSaved={refresh}
      />
    </div>
  );
};

export default MenuItemsSection;
